import { AfterViewInit, Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { ProductModel } from '../../product-model';
import { Entity } from '../../../entity/entity';
import { EntityLine } from '../../../entity/entity-line';
import { EntityLineHeader } from '../../../entity/entity-line-header';
import { TagInformation } from '../../../model/tag-information';
import { GuruEntity } from '../../../model/guru-entity';
import { Sku } from '../../../model/sku';
import { SfrEntry } from '../../../model/sfr-entry';
import { IecLeadTime } from '../../../model/iec-lead-time';
import { ConfigurationService } from '../../configuration.service';
import { ModelService } from '../../../services/model-service';
import { EntityLineService } from '../../../services/entity-line-service';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { StringUtils } from '../../../utils/string-utils';
import { SelectionUtils } from '../../selection-utils';
import { ConfigurationSaveInformation } from '../../../model/configuration-save-information';
import { EntityKeyUtils } from '../../../utils/entity-key-utils';
import { ClmComAccessoriesComponent } from '../common/clm-com-accessories/clm-com-accessories.component';
import { ClmComRatingsComponent } from '../common/clm-com-ratings/clm-com-ratings.component';
import { ClmProductLineHeaderComponent } from '../common/clm-product-line-header/clm-product-line-header.component';

import { SkusAndPricesComponent } from '../../common/skus-and-prices/skus-and-prices.component';
import { ClmCoolingPerformanceInputComponent } from '../common/clm-cooling-performance-input/clm-cooling-performance-input.component';
import { ClmCoolingPerformanceOutputComponent } from '../common/clm-cooling-performance-output/clm-cooling-performance-output.component';
import { ClmHeatingPerformanceInputComponent } from '../common/clm-heating-performance-input/clm-heating-performance-input.component';
import { ClmHeatingPerformanceOutputComponent } from '../common/clm-heating-performance-output/clm-heating-performance-output.component';
import { ClmPerformanceHeaderInputComponent } from '../common/clm-performance-header-input/clm-performance-header-input.component';
import { ClmPerformanceResponse } from '../../../model/clm-performance-response';
import { ClmPerformanceService } from '../../../services/clm-performance.service';
import { PerformanceDefaultsService } from '../../../services/performance-defaults.service';
import { EntityPerformanceDefaults } from '../../../model/entity-performance-defaults';
import { ClmPerformanceOutput } from '../../../model/clm-performance-output';
import { PerformanceDefaults } from '../../../model/performance-defaults';
import { ConfigurationData } from '../../../model/configuration-data';

@Component({
  selector: 'app-tranquility-24-sy-com-product-model',
  imports: [
    ClmComAccessoriesComponent,
    ClmComRatingsComponent,
    ClmProductLineHeaderComponent,
    SkusAndPricesComponent,
    ClmCoolingPerformanceInputComponent,
    ClmCoolingPerformanceOutputComponent,
    ClmHeatingPerformanceInputComponent,
    ClmHeatingPerformanceOutputComponent,
    ClmPerformanceHeaderInputComponent,
  ],
  templateUrl: './tranquility-24-sy-com-product-model.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './tranquility-24-sy-com-product-model.component.css',
})
export class Tranquility24SyComProductModelComponent implements AfterViewInit, ProductModel, OnInit, OnDestroy {
  private readonly configurationService = inject(ConfigurationService);
  private readonly modelService = inject(ModelService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly performanceService = inject(ClmPerformanceService);
  private readonly performanceDefaultsService = inject(PerformanceDefaultsService);
  private readonly engineKey = signal<string>(undefined!);
  private modelName = 'Tranquility24-SY-COM';
  private defaultsSubscription!: Subscription;
  entity!: Entity;
  readonly entityLine = signal<EntityLine>(undefined!);
  entityLineHeader!: EntityLineHeader;
  readonly taggingDetails = signal<TagInformation[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  isLoaded!: boolean;
  readonly errorMessage = signal<string>(undefined!);
  imagePrefix = 'assets/configurator/CLM/Tranquility24-SY-COM/';
  readonly unitImagePath = signal<string>(undefined!);
  readonly invalidEntities = signal<string[]>(undefined!);
  readonly selectionEntities = signal<GuruEntity[]>(undefined!);
  messages!: string[];
  readonly skus = signal<Sku[]>(undefined!);
  readonly sfrEntries = signal<SfrEntry[]>(undefined!);
  entityLeadTime!: IecLeadTime;
  readonly configurationLeadTime = signal<IecLeadTime>(undefined!);
  slowestLeadTime!: IecLeadTime;
  readonly performanceResponse = signal<ClmPerformanceResponse>(undefined!);

  ngOnInit(): void {
    this.defaultsSubscription = this.performanceDefaultsService.getEntityDefaultsChangedObservable().subscribe({
      next: (entityDefaults: EntityPerformanceDefaults) => {
        this.isBusy.set(true);
        this.configurationService.isBusy.set(true);
        this.performanceService.initializePerformanceValues(this.engineKey(), entityDefaults.performanceDefaults, this.selectionEntities()).then(
          () => {
            this.updateUnitPerformance()
              .catch((error: any) => this.handlePerformanceError(error))
              .finally(() => {
                this.configurationService.changeLine(this.entityLine().lineId);
                this.configurationService.isBusy.set(false);
                this.isBusy.set(false);
              });
          },
          (error: any) => this.handleError(error),
        );
      },
      error: () => this.defaultsSubscription.unsubscribe(),
    });
  }

  private handlePerformanceError(error: any): void {
    this.performanceResponse.set(new ClmPerformanceResponse());
    this.performanceResponse().coolingData = new ClmPerformanceOutput();
    this.performanceResponse().coolingData.messages = [];
    this.performanceResponse().coolingData.messages.push(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
    this.configurationService.isBusy.set(false);
  }

  ngOnDestroy(): void {
    this.defaultsSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.isLoaded = true;
    this.isBusy.set(false);
    this.loadMessagesAndSkus();
  }

  initialize(isNew: boolean): Observable<string> {
    const subject = new Subject<string>();
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.loadConfiguration(this.modelName, this.entityLineHeader, this.entity.leadTimeCode).subscribe({
      next: (response) => {
        this.engineKey.set(response.engineKey);
        this.entityLine.set(response.line);
        this.taggingDetails.set(response.taggingDetails);
        this.sfrEntries.set(response.sfrEntries);
        this.modelService.getCharacteristicValue(this.engineKey(), 'USModel', 'FileName').subscribe({
          next: (result) => {
            this.unitImagePath.set(this.imagePrefix + result);
            this.modelService.getInvalidEntities(this.engineKey()).subscribe({
              next: (invalidEntities) => {
                this.invalidEntities.set(invalidEntities);
                this.modelService.getEntitiesStartingWithName(this.engineKey(), 'US').subscribe({
                  next: (entities) => {
                    this.selectionEntities.set(entities);
                    this.loadPerformanceDefaults(isNew).then(
                      () => {
                        subject.next('Initialization Complete');
                      },
                      (error: any) => this.handlePerformanceInitializationError(error, subject),
                    );
                  },
                  error: (error) => this.handleSubjectError(error, subject),
                });
              },
              error: (error) => this.handleSubjectError(error, subject),
            });
          },
          error: (error) => this.handleSubjectError(error, subject),
        });
      },
      error: (error) => this.handleSubjectError(error, subject),
    });
    return subject.asObservable();
  }

  private handlePerformanceInitializationError(error: any, subject: Subject<any>): void {
    this.handlePerformanceError(error);
    subject.next('Rating errors encountered');
  }

  private async loadPerformanceDefaults(isNew: boolean): Promise<void> {
    const entityId: string = this.entity.id.entityId;
    const revision: number = this.entity.id.entityRevision;
    const performanceDefaults: PerformanceDefaults = await firstValueFrom<PerformanceDefaults>(this.performanceDefaultsService.getDefaultValues(entityId, revision));
    await this.performanceService.initializePerformanceValues(this.engineKey(), performanceDefaults, this.selectionEntities(), isNew);
    await this.updateUnitPerformance();
  }

  private handleSubjectError(error: any, subject: Subject<any>): void {
    this.handleError(error);
    subject.error(error);
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
    this.configurationService.isBusy.set(false);
  }

  getEngineKey(): string {
    return this.engineKey();
  }

  quantityChanged(): void {
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  updateTaggingDetails(details: TagInformation[]): void {
    this.errorMessage.set(null!);
    this.entityLine().quantityExtended = details.length;
    this.entityLine().quantity = this.entityLine().quantityExtended + this.entityLine().quantityCancelled;
    this.taggingDetails.set(details);
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  private determineBestLeadTime(asConfigured: IecLeadTime): IecLeadTime {
    return this.sfrsAreApplied() ? this.slowestLeadTime : asConfigured;
  }

  private sfrsAreApplied(): boolean {
    return this.sfrEntries() && this.sfrEntries().length > 0 && this.sfrEntries().filter((sfr) => !sfr.ignore).length > 0;
  }

  getImagePrefix(): string {
    return this.imagePrefix;
  }

  isOem(): boolean {
    return SelectionUtils.isOem(this.entity.prnField1);
  }

  getSaveInformation(): ConfigurationSaveInformation {
    const information = new ConfigurationSaveInformation();
    information.engineKey = this.engineKey();
    information.entityKey = EntityKeyUtils.convertToFullEntityKey(this.entity.id);
    information.lineId = this.entityLine().lineId;
    information.sfrEntries = this.sfrEntries();
    information.taggingInformation = this.taggingDetails();
    information.customerItemId = this.entityLine().customerItemId;
    information.poAmount = this.entityLine().customerPoPrice;
    information.prnField1 = this.entity.prnField1;
    information.initialUnitPrice = SelectionUtils.getConfigurationPrice(this.skus(), this.sfrEntries());
    return information;
  }

  saveUnit(): void {
    this.errorMessage.set(null!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.updateEnginePerformanceValues().then(
      () => {
        this.configurationService.saveConfiguration(this.getSaveInformation()).subscribe({
          next: () => {
            this.entityLineService.getFullEntityLine(this.entityLineHeader).subscribe((line) => {
              this.entityLine.set(line);
              this.entityLineHeader.configurationComplete = line.configurationComplete;
              this.entityLineHeader.quantity = line.quantityExtended;
              this.entityLineHeader.tags = line.externalNotes;
              this.entityLineHeader.description = line.lineDescription;
              this.isBusy.set(false);
              this.configurationService.isBusy.set(false);
            });
          },
          error: (error) => this.handleError(error),
        });
      },
      (error: any) => this.handleError(error),
    );
  }

  async updateEnginePerformanceValues(): Promise<void> {
    if (this.performanceResponse()) {
      await this.performanceService.setPerformanceResponseValues(this.engineKey(), this.performanceResponse(), this.selectionEntities());
    }
  }

  async applyMassUpdate(entities: GuruEntity[]): Promise<void> {
    await this.configurationService.applyMassUpdate(this.engineKey(), entities);
    await this.runUnitValidation();
  }

  validateUnit(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.isBusy.set(true);
    this.runUnitValidation()
      .then(() => (this.configurationService.isBusy.set(false)))
      .catch((error: any) => this.handleError(error));
  }

  private async runUnitValidation(): Promise<void> {
    const validationResponse = await firstValueFrom<any>(this.configurationService.validateUnit(this.engineKey()));
    if (validationResponse) {
      this.invalidEntities.set(validationResponse.invalidEntities);
      this.selectionEntities.set(validationResponse.userSelections);
      this.configurationService.changeLine(this.entityLine().lineId);
      await this.getMessagesAndSkuValues();
      await this.updateUnitPerformance().catch((error: any) => this.handlePerformanceError(error));
    }
  }

  private loadMessagesAndSkus(): void {
    this.configurationService.isBusy.set(true);
    this.getMessagesAndSkuValues()
      .then(() => (this.configurationService.isBusy.set(false)))
      .catch((error: any) => this.handleError(error));
  }

  private async getMessagesAndSkuValues(): Promise<void> {
    const messages: string[] = await firstValueFrom<string[]>(this.configurationService.getMessages(this.engineKey()));
    this.messages = null!;
    if (messages && messages.length > 0) {
      this.messages = messages.filter((message) => message && message.trim().length > 0);
    }
    const data: ConfigurationData = await firstValueFrom<ConfigurationData>(
      this.modelService.getConfigurationData(this.engineKey(), this.entity.priceBasisDate, this.entity.priceListId),
    );
    if (data) {
      this.configurationLeadTime.set(this.determineBestLeadTime(data.bestLeadTime));
      this.skus.set(data.skus);
    }
  }

  sfrFlagChanged(): void {
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  private async updateUnitPerformance(): Promise<void> {
    this.performanceResponse.set(undefined!);
    this.performanceResponse.set(await this.performanceService.getUnitPerformance(this.engineKey(), this.selectionEntities()));
  }
}
